/**
 * Run one job per item, a few at a time, and report what failed.
 *
 * Adding a folder's worth of clips to the library is N calls to POST /clip-labels, and each
 * one is far from free: the API downloads the clip's transcoded preview, runs an ffmpeg pass
 * over the whole file to measure its sound, runs a second ffmpeg for the poster frame, and
 * uploads the result — all inside the request, on the shared `api` pod. Firing them all at
 * once is how you take the dashboard's API down for everybody.
 *
 * So: a small pool, not `Promise.all`. And a pool that CANNOT reject — one clip failing must
 * not abandon the twenty behind it, which is exactly what `Promise.all` would do.
 */

// Three concurrent adds. This is a fact about what POST /clip-labels costs per call, not a
// general-purpose default, which is why it lives here next to the pool that enforces it
// rather than in a shared utils module where it would get reused blind.
export const BULK_ADD_CONCURRENCY = 3;

export interface BulkAddFailure<T> {
  item: T;
  message: string;
}

export interface BulkAddResult<T> {
  succeeded: T[];
  failed: BulkAddFailure<T>[];
}

export interface BulkAddOptions {
  concurrency?: number;
  // Called once per finished item, succeeded or failed, so a progress bar can be determinate.
  onProgress?: (done: number, total: number) => void;
  // Checked between items. Requests already in flight are left to finish — cancelling means
  // "start nothing more", not "abandon a clip mid-write".
  shouldStop?: () => boolean;
}

export async function runBulkAdd<T>(
  items: T[],
  add: (item: T) => Promise<void>,
  { concurrency = BULK_ADD_CONCURRENCY, onProgress, shouldStop }: BulkAddOptions = {},
): Promise<BulkAddResult<T>> {
  const succeeded: T[] = [];
  const failed: BulkAddFailure<T>[] = [];
  let cursor = 0;
  let done = 0;

  async function worker(): Promise<void> {
    for (;;) {
      if (shouldStop?.()) return;
      const index = cursor;
      cursor += 1;
      if (index >= items.length) return;
      const item = items[index];
      try {
        await add(item);
        succeeded.push(item);
      } catch (err: any) {
        // The useful failures here are specific — "this clip is not in the clip index yet",
        // "outSec is past the end of the clip" — so keep the server's words rather than
        // flattening every one of them into "could not add".
        failed.push({ item, message: err?.response?.data?.error || err?.message || 'Could not add this clip' });
      }
      done += 1;
      onProgress?.(done, items.length);
    }
  }

  await Promise.all(
    Array.from({ length: Math.max(1, Math.min(concurrency, items.length)) }, () => worker()),
  );
  return { succeeded, failed };
}
